<script setup lang="ts">
import IconRatingStar from '~theme/components/atoms/icons/IconRatingStar.vue';

const DEFAULT_STEP = 0.1;

// eslint-disable-next-line vue/define-macros-order
const props = withDefaults(
  defineProps<{
    size?: number;
    gap?: number;
    rate?: number;
    max?: number;
    step?: number;
    hideRate?: boolean;
  }>(),
  {
    size: 16,
    gap: 8,
    max: 5,
    rate: 0,
    step: DEFAULT_STEP,
    hideRate: false,
  }
);

const sizePx = computed((): string => `${props.size}px`);
const normalizedMax = computed((): number => Math.floor(props.max));
const normalizedRate = computed((): number =>
  Math.min(Math.max(props.rate, 0), normalizedMax.value)
);
const normalizedStep = computed((): number =>
  props.step >= 0 ? Math.min(props.step, 1) : DEFAULT_STEP
);
const rates = computed((): number[] => {
  const fraction =
    Math.round((normalizedRate.value % 1) / normalizedStep.value) *
    normalizedStep.value;
  return Array.from({ length: normalizedMax.value }, (_, index) => {
    if (index + 1 <= normalizedRate.value) {
      return 1;
    }
    return index < normalizedRate.value ? fraction : 0;
  });
});

const rateString = computed((): string => props.rate.toFixed(1));
</script>

<template>
  <div :class="$style.rating">
    <div :class="[$style.rating__stars, $style.stars]">
      <IconRatingStar
        v-for="(rateOfStar, index) in rates"
        :key="index"
        :class="$style.stars__item"
        :style="{
          width: sizePx,
          height: sizePx,
          fontSize: sizePx,
          lineHeight: sizePx,
          marginLeft: index > 0 ? `${gap}px` : 0,
        }"
        :rate="rateOfStar"
      />
    </div>
    <slot v-if="!hideRate" name="rate">
      <p
        :class="$style.rating__point"
        :style="{
          fontSize: `${(size / 8) * 7}px`,
          marginLeft: `${(size / 4) * 3}px`,
        }"
      >
        {{ rateString }}
      </p>
    </slot>
  </div>
</template>

<style lang="scss" module>
.stars {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.rating {
  display: inline-flex;
  align-items: center;

  &__point {
    // for spa start
    margin: 0;
    // for spa end
    color: $color-text-and-object-accent-yellow;
    font-weight: bold;
  }
}
</style>
